import type { QueryClient } from '@tanstack/react-query';
import { FARMS_QUERY_KEY, ZONES_QUERY_KEY } from 'constants/queryKeys';
import type { IFarm } from 'interfaces/IFarm';
import { ZoneLinkType } from 'interfaces/IInput';
import type { IZone } from 'interfaces/IZone';

/** Discriminates a Farm from a Zone in the mixed IFarmWriteResponse.data array. */
export const isFarm = (item: IFarm | IZone): item is IFarm => 'paddocks' in item;
export const isZone = (item: IFarm | IZone): item is IZone => 'zoneType' in item;

/**
 * Writes a saved farm into the org's farms cache so the Paddocks lens and the
 * tree update without a refetch. The server-returned farm document is
 * authoritative, so no invalidation is needed. A farm not yet in the cache
 * (a new one) is appended (OP-2481).
 */
export const writeFarmToCache = (
  queryClient: QueryClient,
  orgId: string,
  farm: IFarm
) => {
  queryClient.setQueryData<IFarm[]>([FARMS_QUERY_KEY, orgId], (current) => {
    if (!current) return current;
    return current.some((item) => item.id === farm.id)
      ? current.map((item) => (item.id === farm.id ? farm : item))
      : [...current, farm];
  });
};

/**
 * Writes one or more zones into the org's zones cache so the map updates
 * without a refetch. The server-returned zone documents are authoritative,
 * so no invalidation is needed.
 */
export const writeZonesToCache = (
  queryClient: QueryClient,
  orgId: string,
  zones: IZone[]
) => {
  queryClient.setQueryData<IZone[]>([ZONES_QUERY_KEY, orgId], (current) => {
    if (!current) return current;
    let updated = current;
    for (const zone of zones) {
      updated = updated.some((item) => item.id === zone.id)
        ? updated.map((item) => (item.id === zone.id ? zone : item))
        : [...updated, zone];
    }
    return updated;
  });
};

/**
 * Removes soft-deleted zones from the org's zones cache. Delete operations
 * return the zone with isActive: false; since the cache only holds active
 * zones, they must be removed rather than written back.
 */
export const removeZonesFromCache = (
  queryClient: QueryClient,
  orgId: string,
  zoneIds: string[]
) => {
  queryClient.setQueryData<IZone[]>([ZONES_QUERY_KEY, orgId], (current) =>
    current?.filter((zone) => !zoneIds.includes(zone.id))
  );
};

/**
 * Returns the IDs of all cached zones linked to any of the given paddock IDs.
 * Covers both default and custom zones via the zone.links reverse-lookup
 * (link.linkType === ZoneLinkType.Paddock && link.linkId === paddockId).
 * Used as a fallback when the delete response carries no changes records.
 */
export const readZoneIdsByPaddockIds = (
  queryClient: QueryClient,
  orgId: string,
  paddockIds: string[]
): string[] => {
  const paddockIdSet = new Set(paddockIds);
  return (
    queryClient
      .getQueryData<IZone[]>([ZONES_QUERY_KEY, orgId])
      ?.filter((zone) =>
        zone.links?.some(
          (l) => l.linkType === ZoneLinkType.Paddock && paddockIdSet.has(l.linkId)
        )
      )
      .map((zone) => zone.id) ?? []
  );
};

/** Reads one farm from the org's farms cache — the latest copy to build on. */
export const readCachedFarm = (
  queryClient: QueryClient,
  orgId: string,
  farmId: string
): IFarm | undefined =>
  queryClient
    .getQueryData<IFarm[]>([FARMS_QUERY_KEY, orgId])
    ?.find((farm) => farm.id === farmId);
